/**
 * Configuration service for accessing application configuration
 * 
 * Runtime Configuration (Production):
 * - Configuration is fetched from /api/config endpoint at app startup
 * - Backend serves config from ECS-injected environment variables
 * - Same Docker image works in all environments (dev, staging, production)
 * 
 * Build-Time Configuration (Local Development):
 * - Dev auth mode bypasses OIDC configuration entirely
 * - VITE_API_URL configures Vite proxy target (vite.config.ts, not browser code)
 * - Browser code always uses relative URLs (/api/*) in both dev and prod
 */

import { isDevAuthMode } from '@/utils/env'

/**
 * Runtime configuration interface
 * Fetched from /api/config endpoint at app startup
 */
export interface RuntimeConfig {
  application: {
    name: string
    brand: string
    environment: string
    region: string
  }
  oidc: {
    authority: string
    client_id: string
    redirect_uri: string
    post_logout_redirect_uri: string
    scope: string
  }
  cognito?: {
    logout_domain: string
    client_id: string
  }
  monitoring?: {
    provider: string
    config: unknown
  }
  imageTag?: string
  pendo?: { apiKey: string } | null
  features?: Record<string, { enabled: boolean }>
}

/**
 * OIDC configuration interface with standard properties
 * Matches react-oidc-context AuthProviderProps exactly
 */
export interface OidcConfig {
  /** OIDC authority URL (identity provider endpoint) */
  authority: string
  /** OAuth 2.0 client identifier */
  client_id: string
  /** OAuth 2.0 redirect URI after authentication */
  redirect_uri: string
  /** OAuth 2.0 response type */
  response_type: 'code'
  /** OAuth 2.0 scopes */
  scope: string
}

/**
 * Cognito-specific extensions interface
 * Non-standard properties specific to AWS Cognito
 * Property names match AuthProvider expectations
 */
export interface CognitoExtensions {
  /** Cognito logout URL (e.g., https://domain.auth.region.amazoncognito.com) */
  cognitoLogoutUrl: string
  /** OAuth 2.0 client identifier (same as OIDC client_id) */
  cognitoClientId: string
}

/**
 * Service for managing application configuration
 */
export class ConfigService {
  private static config: RuntimeConfig | null = null
  private static initPromise: Promise<void> | null = null

  /**
   * Initialize configuration by fetching from /api/config
   * Call once at app startup before rendering React
   * Idempotent: subsequent calls return cached promise
   * 
   * @throws Error if fetch fails or validation fails
   */
  static async initialize(): Promise<void> {
    // Dev auth mode: OIDC is faked, so skip the OIDC-validated init path — but
    // still load /api/config so the runtime feature-flag map (config.features)
    // is available client-side (e.g. the collaboration gate). Best-effort;
    // failures are non-fatal in dev.
    if (this.isDevAuthMode()) {
      try {
        const response = await fetch('/api/config')
        if (response.ok) {
          this.config = (await response.json()) as RuntimeConfig
        }
      } catch {
        // ignore — feature flags simply stay off in dev if config is unreachable
      }
      return
    }

    // Return cached promise if already initializing/initialized
    if (this.initPromise) {
      return this.initPromise
    }
    
    // Create and cache initialization promise
    this.initPromise = (async (): Promise<void> => {
      try {
        const response = await fetch('/api/config')
        
        if (!response.ok) {
          throw new Error(
            `Failed to fetch /api/config: ${response.status} ${response.statusText}`
          )
        }
        
        const config: RuntimeConfig = await response.json()
        
        // Validate config structure
        if (!config.oidc || !config.oidc.authority || !config.oidc.client_id) {
          throw new Error('Invalid runtime config: missing required OIDC properties')
        }
        
        this.config = config
      } catch (error) {
        // Clear promise on error to allow retry
        this.initPromise = null
        throw error
      }
    })()
    
    await this.initPromise
  }

  /**
   * Get OIDC config for AuthProvider
   * Returns null in dev mode or if not initialized
   * 
   * @returns OidcConfig for react-oidc-context AuthProvider
   */
  static getOidcConfig(): OidcConfig | null {
    // Dev mode bypass
    if (this.isDevAuthMode()) {
      return null
    }
    
    // Not initialized yet
    if (!this.config) {
      return null
    }
    
    // Transform RuntimeConfig.oidc to OidcConfig for AuthProvider
    return {
      authority: this.config.oidc.authority,
      client_id: this.config.oidc.client_id,
      redirect_uri: this.config.oidc.redirect_uri,
      response_type: 'code',  // Fixed value for AuthProvider
      scope: this.config.oidc.scope
    }
  }

  /**
   * Get Cognito-specific extensions
   * Returns null in dev mode or if cognito config not configured
   * 
   * @returns CognitoExtensions with cognitoLogoutUrl and cognitoClientId
   */
  static getCognitoExtensions(): CognitoExtensions | null {
    // Dev mode bypass
    if (this.isDevAuthMode()) {
      return null
    }
    
    // Not initialized or no cognito config
    if (!this.config?.cognito) {
      return null
    }
    
    // Map backend property names to AuthProvider property names
    return {
      cognitoLogoutUrl: this.config.cognito.logout_domain,
      cognitoClientId: this.config.cognito.client_id
    }
  }

  /**
   * Checks if the application is running in development authentication mode
   * @returns True if VITE_DEV_AUTH_MODE is set to 'true'
   */
  static isDevAuthMode(): boolean {
    return isDevAuthMode()
  }

  /**
   * Get monitoring configuration for error tracking and logging
   * Returns null if not configured
   *
   * @returns Monitoring config object with provider and config, or null
   */
  static getMonitoringConfig(): { provider: string; config: unknown } | null {
    return this.config?.monitoring || null
  }

  /** Pendo web-install apiKey, or null when Pendo isn't configured. */
  static getPendoApiKey(): string | null {
    return this.config?.pendo?.apiKey || null
  }

  /**
   * Gets the deployment environment (dev/stg/prd)
   * Returns 'dev' as fallback for local dev mode
   */
  static getEnvironment(): string {
    return this.config?.application.environment || 'dev'
  }

  /**
   * Gets the image tag for release tracking
   * Returns 'dev-local' as fallback for local dev mode
   */
  static getImageTag(): string {
    return this.config?.imageTag || 'dev-local'
  }

  /**
   * Gets the API base URL for browser requests
   *
   * Always returns '/api' (relative URL) because:
   * - Development: Vite proxy intercepts /api/* and forwards to backend
   * - Production: Frontend served by backend, /api/* goes to same origin
   *
   * Note: VITE_API_URL is used by vite.config.ts (Node.js proxy target),
   * NOT by browser code. Browser code should always use relative URLs.
   *
   * @returns API base URL (always '/api')
   */
  static getApiBaseUrl(): string {
    return '/api'
  }

  /**
   * Check if a feature is enabled
   * Returns false if config not initialized, feature not found, or feature disabled
   *
   * @param name Feature flag name (e.g., 'collaboration')
   * @returns True if feature is enabled, false otherwise
   */
  static isFeatureEnabled(name: string): boolean {
    return this.config?.features?.[name]?.enabled ?? false
  }

  /**
   * The backend's RESOLVED verdict for a feature, or `undefined` when it has no
   * opinion — config not loaded yet, or the feature absent from the map.
   *
   * Distinct from `isFeatureEnabled`, which collapses "not loaded" into `false`.
   * The registry needs the difference: a failed `/api/config` must fall back to
   * the static flags rather than blanking the entire navigation.
   */
  static getResolvedFeature(name: string): boolean | undefined {
    return this.config?.features?.[name]?.enabled
  }
}
